import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, from, Observable, of, retry } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ReduceMap } from '@libs/reduce-map';
import { environment } from '@apps/clos-sessions/environments';
import { RevenueCatClient } from '@apps/clos-sessions/services/revenue-cat-client';
import type * as RevenueCat from '@apps/clos-sessions/services/revenue-cat-client/types';
import { ConfirmPurchaseCompletionInput, EntitlementDTO, PurchaseInput } from './types.dto';
import {
  ClosCustomerInfo,
  ClosEntitlement,
  ClosProduct,
  ClosProductType,
  ClosSubscriptionPeriod,
} from './types';

@Injectable({
  providedIn: 'root',
})
export class PurchasesApi {
  protected baseUrl = `${environment.api.baseUrl}/v1/subscription`;

  constructor(
    protected http: HttpClient,
    protected revenueCat: RevenueCatClient,
  ) {}

  getProducts(): Observable<ClosProduct[]> {
    return from(this.revenueCat.getOfferings()).pipe(
      map((offerings) => {
        const offering = this.resolveOffering(offerings);
        if (!offering) {
          return [];
        }

        return offering.availablePackages
          .map((pkg) => this.toClosProduct(pkg))
          .sort((a, b) => a.price.micros - b.price.micros);
      }),
    );
  }

  purchase({ product, customerDetails }: PurchaseInput): Observable<ClosCustomerInfo> {
    return from(this.revenueCat.getOfferings()).pipe(
      switchMap((offerings) => {
        const offering = this.resolveOffering(offerings);
        const rcPackage = offering?.availablePackages.find(
          ({ webBillingProduct }) => webBillingProduct.identifier === product.id,
        );

        if (!rcPackage) {
          throw new Error(`[RevenueCat] Package not found for product: ${product.id}`);
        }

        return this.revenueCat.purchase({
          rcPackage,
          customerEmail: customerDetails.email,
          skipSuccessPage: true,
        });
      }),
      map((result) => this.toClosCustomerInfo(result.customerInfo)),
    );
  }

  // techdebt https://github.com/CLOS-Inc/CLOS_Frontend/issues/544
  confirmPurchaseCompletion(_: ConfirmPurchaseCompletionInput): Observable<void> {
    return this.http.get<{ space: { total: number } }>(`${environment.api.baseUrl}/v1/space`).pipe(
      map(({ space }) => {
        const isSpaceAllocated = space.total > 1073741824;
        if (isSpaceAllocated) {
          return;
        }

        throw new Error(`[Clos API hasn't been completed purchase]`);
      }),
      retry({ delay: 3000, count: 10 }),
    );
  }

  getCustomerInfo(): Observable<ClosCustomerInfo> {
    return combineLatest([
      from(this.revenueCat.getCustomerInfo()).pipe(
        map((result) => {
          return this.toClosCustomerInfo(result);
        }),
      ),
      this.getOuterStoresEntitlements(),
    ]).pipe(
      map(([customerInfo, outerEntitlements]) => {
        const entitlementsByProductMap = new ReduceMap([
          ...outerEntitlements,
          ...customerInfo.entitlements,
        ]).resolveBy('productId');

        const entitlements = Object.values(entitlementsByProductMap);
        return {
          ...customerInfo,
          entitlements,
        };
      }),
    );
  }

  protected getOuterStoresEntitlements(): Observable<ClosEntitlement[]> {
    return this.http.get<EntitlementDTO | null>(`${this.baseUrl}/info`).pipe(
      map((response) => {
        if (response && response.subscription_id) {
          const entitlement: ClosEntitlement = {
            id: 'clos_common_entitlement',
            productId: response.subscription_id,
            isActive: new Date(response.expires_at).getTime() > new Date().getTime(),
            expiresAt: response.expires_at,
            store: { type: 'unknown' },
          };

          return [entitlement];
        }

        return [];
      }),
      // techdebt https://github.com/CLOS-Inc/CLOS_Frontend/issues/546
      catchError(() => of([])),
    );
  }

  protected toClosCustomerInfo(input: RevenueCat.CustomerInfo): ClosCustomerInfo {
    return {
      managementURLs: input.managementURL ? [input.managementURL] : [],
      entitlements: Object.values(input.entitlements.all).map((entitlement) => {
        return this.toClosEntitlement(entitlement);
      }),
    };
  }

  protected toClosEntitlement(input: RevenueCat.EntitlementInfo): ClosEntitlement {
    return {
      id: input.identifier,
      productId: input.productIdentifier,
      isActive: input.isActive,
      expiresAt: input.expirationDate?.toISOString() ?? '',
      store: { type: input.store },
    };
  }

  /**
   * Prefer an explicitly configured offering (e.g. the Paddle one) over the
   * project-global `current` offering, which is shared with the mobile apps.
   */
  protected resolveOffering(offerings: RevenueCat.Offerings): RevenueCat.Offering | null {
    const { offeringId } = environment.revenuecat as { offeringId?: string };

    return (offeringId && offerings.all[offeringId]) || offerings.current;
  }

  protected toClosProduct(pkg: RevenueCat.Package): ClosProduct {
    const product = pkg.webBillingProduct;

    return {
      id: product.identifier,
      title: product.title,
      description: product.description || '',
      price: {
        micros: product.price.amountMicros,
        currency: product.price.currency,
        formattedPrice: product.price.formattedPrice,
      },
      type: ClosProductType.Subscription,
      period: this.toClosSubscriptionPeriod(product.period?.unit),
      // RC typings: `webBillingProduct.metadata` is `Record<string, string> | null | undefined`.
      // Normalize to an empty object so downstream selectors can treat it as required.
      metadata: (product as { metadata?: Record<string, string> | null }).metadata ?? {},
    };
  }

  protected toClosSubscriptionPeriod(
    unit: RevenueCat.PeriodUnit | undefined,
  ): ClosSubscriptionPeriod {
    if (!unit) {
      throw new Error(`[RevenueCat] Invalid period unit: ${unit}`);
    }

    return unit as unknown as ClosSubscriptionPeriod;
  }
}
